import React from "react";
import {
  Document, Page, Text, View, StyleSheet, Font, pdf,
} from "@react-pdf/renderer";

const BLUE   = "#1D4ED8";
const DARK   = "#0F172A";
const CYAN   = "#0EA5E9";
const GRAY   = "#444444";
const LGRAY  = "#666666";
const BORDER = "#CCCCCC";

// ─── ATS Safe PDF ────────────────────────────────────────────────────────────

const atsStyles = StyleSheet.create({
  page:       { fontFamily: "Helvetica", fontSize: 10, padding: 40, color: "#111" },
  name:       { fontSize: 18, fontFamily: "Helvetica-Bold", textAlign: "center", marginBottom: 2 },
  contact:    { fontSize: 9, textAlign: "center", color: GRAY, marginBottom: 14 },
  secHeader:  { fontFamily: "Helvetica-Bold", fontSize: 9, textTransform: "uppercase", letterSpacing: 1.5, borderBottomWidth: 1, borderBottomColor: "#111", paddingBottom: 2, marginBottom: 6, marginTop: 10 },
  roleTitle:  { fontFamily: "Helvetica-Bold", fontSize: 10 },
  roleDate:   { fontSize: 9, color: LGRAY },
  roleMeta:   { fontSize: 9, color: LGRAY, marginBottom: 2 },
  bullet:     { fontSize: 10, marginLeft: 10, marginBottom: 1.5 },
  skillRow:   { flexDirection: "row", marginBottom: 2 },
  skillLabel: { fontFamily: "Helvetica-Bold", fontSize: 10, width: 90 },
  skillVal:   { fontSize: 10, flex: 1, color: GRAY },
  row:        { flexDirection: "row", justifyContent: "space-between", alignItems: "flex-start" },
});

export function ATSPdf({ data }) {
  const c = data?.contact || {};
  const contactParts = [c.email, c.phone, c.location, c.linkedin].filter(Boolean);
  return (
    <Document>
      <Page size="LETTER" style={atsStyles.page}>
        {c.name && <Text style={atsStyles.name}>{c.name}</Text>}
        {contactParts.length > 0 && <Text style={atsStyles.contact}>{contactParts.join("  |  ")}</Text>}

        {data?.summary && (
          <><Text style={atsStyles.secHeader}>Professional Summary</Text><Text style={{ fontSize: 10, color: GRAY }}>{data.summary}</Text></>
        )}

        {data?.experience?.length > 0 && (
          <><Text style={atsStyles.secHeader}>Work Experience</Text>
          {data.experience.map((exp, i) => (
            <View key={i} style={{ marginBottom: 8 }}>
              <View style={atsStyles.row}>
                <Text style={atsStyles.roleTitle}>{exp.title} — {exp.company}</Text>
                <Text style={atsStyles.roleDate}>{exp.start_date} – {exp.end_date}</Text>
              </View>
              {exp.location && <Text style={atsStyles.roleMeta}>{exp.location}</Text>}
              {(exp.bullets || []).map((b, j) => <Text key={j} style={atsStyles.bullet}>•  {b}</Text>)}
            </View>
          ))}</>
        )}

        {data?.education?.length > 0 && (
          <><Text style={atsStyles.secHeader}>Education</Text>
          {data.education.map((edu, i) => (
            <View key={i} style={{ marginBottom: 6 }}>
              <View style={atsStyles.row}>
                <Text style={atsStyles.roleTitle}>{edu.degree}</Text>
                <Text style={atsStyles.roleDate}>{edu.graduation_date}</Text>
              </View>
              <Text style={atsStyles.roleMeta}>{edu.institution}{edu.location ? ` · ${edu.location}` : ""}</Text>
              {(edu.details || []).map((d, j) => <Text key={j} style={{ fontSize: 9, color: LGRAY }}>•  {d}</Text>)}
            </View>
          ))}</>
        )}

        {data?.skills?.length > 0 && (
          <><Text style={atsStyles.secHeader}>Skills</Text>
          {data.skills.map((sk, i) => (
            <View key={i} style={atsStyles.skillRow}>
              <Text style={atsStyles.skillLabel}>{sk.name}:</Text>
              <Text style={atsStyles.skillVal}>{(sk.items || []).join(", ")}</Text>
            </View>
          ))}</>
        )}

        {data?.certifications?.length > 0 && (
          <><Text style={atsStyles.secHeader}>Certifications</Text>
          {data.certifications.map((cert, i) => (
            <Text key={i} style={{ fontSize: 10, marginBottom: 2 }}>
              {[cert.name, cert.issuer, cert.date].filter(Boolean).join("  |  ")}
            </Text>
          ))}</>
        )}
      </Page>
    </Document>
  );
}

// ─── Professional PDF ─────────────────────────────────────────────────────────

const proStyles = StyleSheet.create({
  page:       { fontFamily: "Helvetica", fontSize: 10, color: "#1a1a1a" },
  header:     { backgroundColor: BLUE, padding: "24 32", marginBottom: 0 },
  headerName: { fontFamily: "Helvetica-Bold", fontSize: 20, color: "white", marginBottom: 3 },
  headerSub:  { fontSize: 9, color: "#BFDBFE" },
  body:       { padding: "16 32" },
  secHeader:  { fontFamily: "Helvetica-Bold", fontSize: 9, textTransform: "uppercase", letterSpacing: 1.5, color: BLUE, borderBottomWidth: 1.5, borderBottomColor: BLUE, paddingBottom: 2, marginBottom: 6, marginTop: 12 },
  roleTitle:  { fontFamily: "Helvetica-Bold", fontSize: 11, color: BLUE },
  roleCompany:{ fontSize: 10, fontFamily: "Helvetica-Oblique", color: "#444", marginBottom: 1 },
  roleDate:   { fontSize: 9, color: LGRAY },
  bullet:     { fontSize: 10, marginLeft: 10, marginBottom: 1.5, color: "#333" },
  row:        { flexDirection: "row", justifyContent: "space-between" },
  twoCol:     { flexDirection: "row", gap: 24 },
  col:        { flex: 1 },
});

export function ProfessionalPdf({ data }) {
  const c = data?.contact || {};
  const contactParts = [c.email, c.phone, c.location, c.linkedin].filter(Boolean);
  return (
    <Document>
      <Page size="LETTER" style={proStyles.page}>
        <View style={proStyles.header}>
          {c.name && <Text style={proStyles.headerName}>{c.name}</Text>}
          {contactParts.length > 0 && <Text style={proStyles.headerSub}>{contactParts.join("  ·  ")}</Text>}
        </View>
        <View style={proStyles.body}>
          {data?.summary && (
            <><Text style={proStyles.secHeader}>Professional Summary</Text>
            <Text style={{ fontSize: 10, color: GRAY }}>{data.summary}</Text></>
          )}

          {data?.experience?.length > 0 && (
            <><Text style={proStyles.secHeader}>Work Experience</Text>
            {data.experience.map((exp, i) => (
              <View key={i} style={{ marginBottom: 10 }}>
                <View style={proStyles.row}>
                  <Text style={proStyles.roleTitle}>{exp.title}</Text>
                  <Text style={proStyles.roleDate}>{exp.start_date} – {exp.end_date}</Text>
                </View>
                <Text style={proStyles.roleCompany}>{exp.company}{exp.location ? ` · ${exp.location}` : ""}</Text>
                {(exp.bullets || []).map((b, j) => <Text key={j} style={proStyles.bullet}>•  {b}</Text>)}
              </View>
            ))}</>
          )}

          <View style={proStyles.twoCol}>
            <View style={proStyles.col}>
              {data?.education?.length > 0 && (
                <><Text style={proStyles.secHeader}>Education</Text>
                {data.education.map((edu, i) => (
                  <View key={i} style={{ marginBottom: 6 }}>
                    <Text style={{ fontFamily: "Helvetica-Bold", fontSize: 10, color: BLUE }}>{edu.degree}</Text>
                    <Text style={{ fontSize: 9, color: "#555" }}>{edu.institution}</Text>
                    <Text style={{ fontSize: 9, color: LGRAY }}>{edu.graduation_date}{edu.location ? ` · ${edu.location}` : ""}</Text>
                  </View>
                ))}</>
              )}
              {data?.certifications?.length > 0 && (
                <><Text style={proStyles.secHeader}>Certifications</Text>
                {data.certifications.map((cert, i) => (
                  <View key={i} style={{ marginBottom: 4 }}>
                    <Text style={{ fontFamily: "Helvetica-Bold", fontSize: 10 }}>{cert.name}</Text>
                    <Text style={{ fontSize: 9, color: LGRAY }}>{[cert.issuer, cert.date].filter(Boolean).join(" · ")}</Text>
                  </View>
                ))}</>
              )}
            </View>
            <View style={proStyles.col}>
              {data?.skills?.length > 0 && (
                <><Text style={proStyles.secHeader}>Skills</Text>
                {data.skills.map((sk, i) => (
                  <View key={i} style={{ marginBottom: 5 }}>
                    <Text style={{ fontFamily: "Helvetica-Bold", fontSize: 9, color: BLUE, textTransform: "uppercase", letterSpacing: 0.8 }}>{sk.name}</Text>
                    <Text style={{ fontSize: 9, color: "#333" }}>{(sk.items || []).join(" · ")}</Text>
                  </View>
                ))}</>
              )}
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
}

// ─── Modern PDF ───────────────────────────────────────────────────────────────

const modStyles = StyleSheet.create({
  page:       { fontFamily: "Helvetica", fontSize: 10, flexDirection: "row", color: "#1a1a1a" },
  sidebar:    { width: 180, backgroundColor: DARK, padding: "28 16", color: "white" },
  sideName:   { fontFamily: "Helvetica-Bold", fontSize: 15, color: "white", marginBottom: 6 },
  sideAccent: { width: 28, height: 2, backgroundColor: CYAN, marginBottom: 14 },
  sideLabel:  { fontFamily: "Helvetica-Bold", fontSize: 8, textTransform: "uppercase", letterSpacing: 1.5, color: CYAN, borderBottomWidth: 0.5, borderBottomColor: "#334155", paddingBottom: 2, marginBottom: 5, marginTop: 10 },
  sideText:   { fontSize: 8.5, color: "#CBD5E1", marginBottom: 1.5 },
  skillCat:   { fontFamily: "Helvetica-Bold", fontSize: 8, color: CYAN, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 1 },
  skillItem:  { fontSize: 8, color: "#CBD5E1", marginBottom: 0.5 },
  main:       { flex: 1, padding: "28 24" },
  secHeader:  { flexDirection: "row", alignItems: "center", marginBottom: 6, marginTop: 12 },
  secAccent:  { width: 3, height: 14, backgroundColor: CYAN, borderRadius: 2, marginRight: 6 },
  secTitle:   { fontFamily: "Helvetica-Bold", fontSize: 9, textTransform: "uppercase", letterSpacing: 1.5, color: DARK },
  roleTitle:  { fontFamily: "Helvetica-Bold", fontSize: 11, color: DARK },
  roleDate:   { fontSize: 8.5, color: CYAN, fontFamily: "Helvetica-Oblique" },
  roleCompany:{ fontSize: 9, color: "#555", fontFamily: "Helvetica-Oblique", marginBottom: 2 },
  bullet:     { flexDirection: "row", marginBottom: 1.5 },
  bulletMark: { fontSize: 9, color: CYAN, marginRight: 5, fontFamily: "Helvetica-Bold" },
  bulletText: { fontSize: 9.5, flex: 1, color: "#333" },
});

export function ModernPdf({ data }) {
  const c = data?.contact || {};
  const contactParts = [c.email, c.phone, c.location, c.linkedin].filter(Boolean);
  return (
    <Document>
      <Page size="LETTER" style={modStyles.page}>
        {/* Sidebar */}
        <View style={modStyles.sidebar}>
          {c.name && <Text style={modStyles.sideName}>{c.name}</Text>}
          <View style={modStyles.sideAccent} />
          {contactParts.length > 0 && (
            <><Text style={modStyles.sideLabel}>Contact</Text>
            {contactParts.map((p, i) => <Text key={i} style={modStyles.sideText}>{p}</Text>)}</>
          )}
          {data?.skills?.length > 0 && (
            <><Text style={modStyles.sideLabel}>Skills</Text>
            {data.skills.map((sk, i) => (
              <View key={i} style={{ marginBottom: 6 }}>
                <Text style={modStyles.skillCat}>{sk.name}</Text>
                {(sk.items || []).map((item, j) => <Text key={j} style={modStyles.skillItem}>· {item}</Text>)}
              </View>
            ))}</>
          )}
          {data?.education?.length > 0 && (
            <><Text style={modStyles.sideLabel}>Education</Text>
            {data.education.map((edu, i) => (
              <View key={i} style={{ marginBottom: 8 }}>
                <Text style={{ fontFamily: "Helvetica-Bold", fontSize: 8.5, color: "white" }}>{edu.degree}</Text>
                <Text style={{ fontSize: 8, color: "#CBD5E1" }}>{edu.institution}</Text>
                <Text style={{ fontSize: 8, color: CYAN }}>{edu.graduation_date}</Text>
              </View>
            ))}</>
          )}
          {data?.certifications?.length > 0 && (
            <><Text style={modStyles.sideLabel}>Certifications</Text>
            {data.certifications.map((cert, i) => (
              <View key={i} style={{ marginBottom: 5 }}>
                <Text style={{ fontFamily: "Helvetica-Bold", fontSize: 8.5, color: "white" }}>{cert.name}</Text>
                {cert.issuer && <Text style={{ fontSize: 8, color: "#94A3B8" }}>{cert.issuer}</Text>}
                {cert.date && <Text style={{ fontSize: 8, color: CYAN }}>{cert.date}</Text>}
              </View>
            ))}</>
          )}
        </View>

        {/* Main */}
        <View style={modStyles.main}>
          {data?.summary && (
            <><View style={modStyles.secHeader}><View style={modStyles.secAccent} /><Text style={modStyles.secTitle}>Profile</Text></View>
            <Text style={{ fontSize: 10, color: "#444" }}>{data.summary}</Text></>
          )}
          {data?.experience?.length > 0 && (
            <><View style={modStyles.secHeader}><View style={modStyles.secAccent} /><Text style={modStyles.secTitle}>Experience</Text></View>
            {data.experience.map((exp, i) => (
              <View key={i} style={{ marginBottom: 10 }}>
                <View style={{ flexDirection: "row", justifyContent: "space-between" }}>
                  <Text style={modStyles.roleTitle}>{exp.title}</Text>
                  <Text style={modStyles.roleDate}>{exp.start_date} – {exp.end_date}</Text>
                </View>
                <Text style={modStyles.roleCompany}>{exp.company}{exp.location ? ` · ${exp.location}` : ""}</Text>
                {(exp.bullets || []).map((b, j) => (
                  <View key={j} style={modStyles.bullet}>
                    <Text style={modStyles.bulletMark}>▸</Text>
                    <Text style={modStyles.bulletText}>{b}</Text>
                  </View>
                ))}
              </View>
            ))}</>
          )}
        </View>
      </Page>
    </Document>
  );
}

// ─── Download helper ──────────────────────────────────────────────────────────

export async function downloadResumePDF(data, template, candidateName) {
  const PdfComponent = template === "professional" ? ProfessionalPdf : template === "modern" ? ModernPdf : ATSPdf;
  const blob = await pdf(<PdfComponent data={data} />).toBlob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${(candidateName || "resume").replace(/\s+/g, "_")}_${template}.pdf`;
  a.click();
  URL.revokeObjectURL(url);
}
